import {
  Blend,
  BlendState,
  Color4,
  Container,
  ExternalSceneObject,
  Filter,
  FilteredSceneObject,
  FilterKind,
  Platform,
  RenderDevice,
  SceneObject,
  T_Platform,
  Vector2,
} from '@cgs/wge';
import { ResourcesComponent } from '../../game/components/resources_component';
import { RESOURCES_COMPONENT_TOKEN } from '../../type_definitions';
import { LAUNCH_PARAMS_STORE_TOKEN, LaunchParamsStore } from '../../store';
import { getCheatUIStyles } from '../cheat/ui/styles';

export const FILTER_EDITOR_POPUP_TOKEN = Symbol('FilterEditorPopup');

const POSITION_STORAGE_KEY = 'filter_editor_panel_position';

const KIND_OPTIONS: Array<{ value: FilterKind; label: string }> = [
  { value: FilterKind.None, label: 'None' },
  { value: FilterKind.Tint, label: 'Tint' },
  { value: FilterKind.Pixelate, label: 'Pixelate' },
  { value: FilterKind.Shockwave, label: 'Shockwave' },
  { value: FilterKind.Noise, label: 'Noise' },
  { value: FilterKind.Twist, label: 'Twist' },
  { value: FilterKind.Bulge, label: 'Bulge' },
  { value: FilterKind.RGBSplit, label: 'RGBSplit (3 samples)' },
  { value: FilterKind.CRT, label: 'CRT' },
  { value: FilterKind.OldFilm, label: 'OldFilm' },
  { value: FilterKind.Outline, label: 'Outline (4 samples)' },
  { value: FilterKind.InnerGlow, label: 'InnerGlow (8 samples ⚠)' },
  { value: FilterKind.Vignette, label: 'Vignette' },
  { value: FilterKind.GodRays, label: 'GodRays (16 samples ⚠⚠)' },
  { value: FilterKind.PseudoBloom, label: 'PseudoBloom (4 samples)' },
  { value: FilterKind.HueShift, label: 'HueShift' },
  { value: FilterKind.Posterize, label: 'Posterize' },
  { value: FilterKind.Flash, label: 'Flash' },
];

const ANIMATED_KINDS = new Set<FilterKind>([
  FilterKind.Shockwave,
  FilterKind.Noise,
  FilterKind.CRT,
  FilterKind.OldFilm,
  FilterKind.Flash,
]);

// Огибающая пульса Flash, снята с референс-видео SweetPop (SP-00_Tension_Win.mp4)
const FLASH_PULSE_PERIOD_SEC = 2.2;
const FLASH_PULSE_ATTACK_SEC = 0.2;
const FLASH_PULSE_DECAY_SEC = 0.4;

// Спавн тестового flash-квада: выделенная белая текстура (не атлас!), аддитивный блендинг
const FLASH_QUAD_SIZE = 512;
const FLASH_QUAD_DEFAULT_POSITION = new Vector2(100, 100);
// Поверх всех детей корня сцены (childs сортируются по z)
const FLASH_QUAD_Z = 100000;
// Псевдо-«сцена» в дропдауне попапа, чтобы спавненные квады можно было найти в списке
const FLASH_QUAD_SCENE_GROUP = 'flash quads';
const FLASH_PRESET_TINT = new Color4(1.0, 0.85, 0.95, 1.0);
const FLASH_PRESET_RADIUS = 0.5;
const FLASH_PRESET_CORE_RATIO = 0.15;
// Кольцо линзового блика: чуть больше видимой вспышки, узкое и явное (референс — key_09_pulse1_peak)
const FLASH_PRESET_RING_RADIUS = 0.36;
const FLASH_PRESET_RING_WIDTH_PCT = 10;
const FLASH_PRESET_RING_INTENSITY = 0.7;

function flashPulseEnvelope(elapsedSec: number): number {
  const t = elapsedSec % FLASH_PULSE_PERIOD_SEC;
  if (t < FLASH_PULSE_ATTACK_SEC) return t / FLASH_PULSE_ATTACK_SEC;
  if (t < FLASH_PULSE_ATTACK_SEC + FLASH_PULSE_DECAY_SEC) {
    const x = 1 - (t - FLASH_PULSE_ATTACK_SEC) / FLASH_PULSE_DECAY_SEC;
    return x * x;
  }
  return 0;
}

interface ObjectEntry {
  id: string;
  sceneUrl: string;
  ref: WeakRef<SceneObject>;
}

export class FilterEditorPopup {
  private rootEl: HTMLDivElement | null = null;
  private isShown = false;
  private isCollapsed = false;

  private snapshot: ObjectEntry[] = [];
  private sceneUrls: string[] = [];
  private selectedRef: WeakRef<SceneObject> | null = null;
  private selectedId = '';
  private searchQuery = '';
  private onlyWithFilter = false;
  private sceneFilter = '';

  private animFrameId: number | null = null;
  private animStart = 0;
  private flashBaseIntensity: number | null = null;
  private flashQuadCounter = 0;
  private renderDevice: RenderDevice | null = null;
  private wrapWidth = 256;
  private wrapHeight = 256;

  constructor(private readonly container: Container) {
    const launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    const platform = container.resolve<Platform>(T_Platform, { optional: true });
    this.renderDevice = platform?.videoSystem.renderDevice ?? null;
    if (launchParamsStore.state.cheatEnabled) {
      this.initializeUI();
    } else {
      const unsubscribe = launchParamsStore.stream.on((p) => {
        if (!p.cheatEnabled) return;
        this.initializeUI();
        unsubscribe();
      });
    }
  }

  private rescanScene(): void {
    const resources = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    const next: ObjectEntry[] = [];
    const urls = new Set<string>();
    const walk = (node: SceneObject): void => {
      if (node.id) {
        const url = node.sceneUrl ?? '';
        next.push({ id: node.id, sceneUrl: url, ref: new WeakRef(node) });
        urls.add(url);
      }
      const children = node.innerChilds;
      if (!children) return;
      for (let i = 0; i < children.length; i++) {
        walk(children[i]);
      }
    };
    walk(resources.root);
    this.snapshot = next;
    this.sceneUrls = Array.from(urls).sort();
  }

  private getSelected(): SceneObject | null {
    if (!this.selectedRef) return null;
    const obj = this.selectedRef.deref();
    if (!obj) {
      this.selectedRef = null;
      this.selectedId = '';
      return null;
    }
    return obj;
  }

  private getFilteredEntries(): { entries: ObjectEntry[]; total: number } {
    if (!this.sceneFilter) return { entries: [], total: 0 };
    const q = this.searchQuery.trim().toLowerCase();
    const out: ObjectEntry[] = [];
    let total = 0;
    for (let i = 0; i < this.snapshot.length; i++) {
      const e = this.snapshot[i];
      if (e.sceneUrl !== this.sceneFilter) continue;
      if (q && !e.id.toLowerCase().includes(q)) continue;
      if (this.onlyWithFilter) {
        const obj = e.ref.deref();
        if (!obj || !obj.filter) continue;
      }
      total++;
      if (out.length < 200) out.push(e);
    }
    return { entries: out, total };
  }

  private initializeUI(): void {
    this.rootEl = document.createElement('div');
    const styles = getCheatUIStyles();
    this.rootEl.style.cssText = styles.popup;
    this.restorePosition();

    this.rootEl.innerHTML = `
      <div class="filter-editor-header" style="${styles.headerStyle}">
        <span style="color:white;font-size:14px;">Filter Editor</span>
        <div style="${styles.buttonContainerStyle}">
          <button id="filterCollapseBtn" style="${styles.headerButtonStyle}" title="Collapse">−</button>
          <button id="filterCloseBtn" style="${styles.headerButtonStyle}" title="Close">×</button>
        </div>
      </div>
      <div id="filter-editor-content" style="max-height:80vh;overflow-y:auto;padding:12px;color:#fff;font:14px Arial,sans-serif;"></div>
    `;
    document.body.appendChild(this.rootEl);

    Object.assign(this.rootEl.style, {
      position: 'fixed',
      top: '20px',
      left: '20px',
      width: '420px',
      maxWidth: '80vw',
      maxHeight: '85vh',
      zIndex: '2147483647',
    } as CSSStyleDeclaration);

    this.initializeDrag();
    this.rootEl
      .querySelector('#filterCollapseBtn')
      ?.addEventListener('click', () => this.toggleCollapse());
    this.rootEl
      .querySelector('#filterCloseBtn')
      ?.addEventListener('click', () => this.setShowState(false));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'f' || e.key === 'F') this.setShowState(!this.isShown);
    });

    this.setShowState(false);
  }

  private toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    const content = this.rootEl?.querySelector<HTMLDivElement>('#filter-editor-content');
    if (content) content.style.display = this.isCollapsed ? 'none' : 'block';
    const btn = this.rootEl?.querySelector<HTMLButtonElement>('#filterCollapseBtn');
    if (btn) btn.textContent = this.isCollapsed ? '+' : '−';
  }

  private setShowState(show: boolean): void {
    this.isShown = show;
    if (this.rootEl) this.rootEl.style.display = show ? 'block' : 'none';
    if (show) {
      this.renderUI();
    } else {
      this.stopAnimLoop();
    }
  }

  private startAnimLoop(): void {
    if (this.animFrameId !== null) return;
    this.animStart = performance.now();
    const startFilter = this.getSelected()?.filter;
    if (startFilter?.kind === FilterKind.Flash) {
      this.flashBaseIntensity = startFilter.intensity;
    }
    const tick = () => {
      const sel = this.getSelected();
      const f = sel?.filter;
      if (f && ANIMATED_KINDS.has(f.kind)) {
        const elapsed = (performance.now() - this.animStart) / 1000;
        if (f.kind === FilterKind.Flash) {
          f.intensity = (this.flashBaseIntensity ?? 1) * flashPulseEnvelope(elapsed);
        } else {
          f.time = elapsed;
        }
      }
      this.animFrameId = requestAnimationFrame(tick);
    };
    this.animFrameId = requestAnimationFrame(tick);
  }

  private stopAnimLoop(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    const f = this.getSelected()?.filter;
    if (f?.kind === FilterKind.Flash && this.flashBaseIntensity !== null) {
      f.intensity = this.flashBaseIntensity;
    }
    this.flashBaseIntensity = null;
  }

  private initializeDrag(): void {
    if (!this.rootEl) return;
    const header = this.rootEl.querySelector('.filter-editor-header');
    if (!header) return;
    let dragging = false;
    let dx = 0;
    let dy = 0;
    header.addEventListener('mousedown', ((e: Event) => {
      const me = e as MouseEvent;
      dragging = true;
      dx = me.clientX - this.rootEl!.offsetLeft;
      dy = me.clientY - this.rootEl!.offsetTop;
    }) as EventListener);
    document.addEventListener('mousemove', ((e: Event) => {
      if (!dragging || !this.rootEl) return;
      const me = e as MouseEvent;
      this.rootEl.style.left = `${me.clientX - dx}px`;
      this.rootEl.style.top = `${me.clientY - dy}px`;
      this.rootEl.style.transform = 'none';
    }) as EventListener);
    document.addEventListener('mouseup', () => {
      if (dragging) {
        dragging = false;
        this.savePosition();
      }
    });
  }

  private savePosition(): void {
    if (!this.rootEl) return;
    const r = this.rootEl.getBoundingClientRect();
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: r.left, y: r.top }));
  }

  private restorePosition(): void {
    if (!this.rootEl) return;
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!saved) return;
    try {
      const { x, y } = JSON.parse(saved);
      this.rootEl.style.left = `${x}px`;
      this.rootEl.style.top = `${y}px`;
      this.rootEl.style.transform = 'none';
    } catch {
      //
    }
  }

  private renderUI(): void {
    const content = this.rootEl?.querySelector<HTMLDivElement>('#filter-editor-content');
    if (!content) return;
    content.innerHTML = '';

    const styles = getCheatUIStyles();

    if (this.snapshot.length === 0) {
      const intro = document.createElement('div');
      intro.style.cssText = styles.sectionStyle;
      intro.innerHTML = `
        <div style="font-size:12px;color:#bbb;margin-bottom:8px;">
          «+ Flash quad» создаёт тестовый квад со вспышкой — скан сцены для этого не нужен.
          Scan scene нужен только чтобы вешать фильтры на существующие объекты: сканирование
          синхронное и держит ссылки через WeakRef — на больших сценах может фризнуть на ~1с.
        </div>
        <div style="display:flex;gap:8px;">
          <button id="filterSpawnFlashBtn" style="${styles.buttonStyle}" ${this.renderDevice ? '' : 'disabled title="RenderDevice not available"'}>+ Flash quad</button>
          <button id="filterScanBtn" style="${styles.buttonStyle}">Scan scene</button>
        </div>
      `;
      content.appendChild(intro);
      intro.querySelector<HTMLButtonElement>('#filterSpawnFlashBtn')?.addEventListener('click', () => {
        this.spawnFlashQuad();
      });
      content.querySelector<HTMLButtonElement>('#filterScanBtn')?.addEventListener('click', () => {
        this.rescanScene();
        this.renderUI();
      });
      return;
    }

    const { entries, total } = this.getFilteredEntries();
    const truncated = total > entries.length;

    const sceneOptions = this.sceneUrls
      .map(
        (url) =>
          `<option value="${escapeAttr(url)}" ${url === this.sceneFilter ? 'selected' : ''}>${escapeHtml(url || '(no scene)')}</option>`
      )
      .join('');

    const search = document.createElement('div');
    search.style.cssText = styles.sectionStyle;
    search.innerHTML = `
      <label style="display:block;font-size:12px;color:#bbb;margin:0 8px 6px;">Scene</label>
      <select id="filterSceneSelect" style="${styles.selectStyle} width:calc(100% - 16px);margin-left:8px;margin-right:8px;">
        <option value="" ${this.sceneFilter === '' ? 'selected' : ''}>— choose scene —</option>
        ${sceneOptions}
      </select>
      <input id="filterSearch" type="text" placeholder="Search by id..." value="${escapeAttr(this.searchQuery)}" style="${styles.inputStyle}"/>
      <label style="display:block;margin:4px 8px 0;font-size:12px;color:#bbb;">
        <input id="filterOnlyWith" type="checkbox" ${this.onlyWithFilter ? 'checked' : ''}/> only with active filter
      </label>
      <div style="display:flex;align-items:center;gap:8px;margin:6px 8px 0;">
        <span style="font-size:11px;color:#888;flex:1;">
          ${this.sceneFilter ? `showing ${entries.length} / ${total}${truncated ? ' (truncated, use search)' : ''}` : 'pick a scene to see objects'} · snapshot ${this.snapshot.length}, scenes ${this.sceneUrls.length}
        </span>
        <button id="filterSpawnFlashBtn" style="${styles.buttonStyle}" ${this.renderDevice ? '' : 'disabled title="RenderDevice not available"'}>+ Flash quad</button>
        <button id="filterRefreshBtn" style="${styles.buttonStyle}" title="Re-scan scene">⟳</button>
      </div>
    `;
    content.appendChild(search);
    search.querySelector<HTMLButtonElement>('#filterSpawnFlashBtn')?.addEventListener('click', () => {
      this.spawnFlashQuad();
    });

    const list = document.createElement('div');
    list.style.cssText = `${styles.sectionStyle} max-height:200px; overflow-y:auto; padding:0;`;
    const selected = this.getSelected();
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const obj = entry.ref.deref();
      const row = document.createElement('div');
      const isSelected = obj === selected && obj !== undefined;
      const alive = obj !== undefined;
      row.style.cssText = `padding:6px 12px; border-bottom:1px solid rgba(255,255,255,0.05); cursor:pointer; font-size:12px; ${isSelected ? 'background:rgba(80,140,255,0.18);' : ''} ${!alive ? 'opacity:0.4;' : ''}`;
      const kindLabel = alive && obj!.filter ? KIND_OPTIONS.find((k) => k.value === obj!.filter!.kind)?.label : null;
      row.innerHTML = `
        <span>${escapeHtml(entry.id)}${alive ? '' : ' <i style="color:#888">(gc)</i>'}</span>
        <span style="float:right;color:${kindLabel ? '#7fdc7a' : '#666'};font-size:10px;">${kindLabel ?? '—'}</span>
      `;
      row.addEventListener('click', () => {
        const cur = entry.ref.deref();
        if (!cur) return;
        this.selectedRef = entry.ref;
        this.selectedId = entry.id;
        this.renderUI();
      });
      list.appendChild(row);
    }
    if (entries.length === 0 && this.sceneFilter) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:12px; color:#888; font-size:12px; text-align:center;';
      empty.textContent = 'no objects match';
      list.appendChild(empty);
    }
    content.appendChild(list);

    if (selected) {
      content.appendChild(this.renderParamsSection(selected));
    }

    content.querySelector<HTMLSelectElement>('#filterSceneSelect')?.addEventListener('change', (e) => {
      this.sceneFilter = (e.target as HTMLSelectElement).value;
      this.selectedRef = null;
      this.selectedId = '';
      this.renderUI();
    });
    content.querySelector<HTMLInputElement>('#filterSearch')?.addEventListener('input', (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.renderUI();
    });
    content.querySelector<HTMLInputElement>('#filterOnlyWith')?.addEventListener('change', (e) => {
      this.onlyWithFilter = (e.target as HTMLInputElement).checked;
      this.renderUI();
    });
    content.querySelector<HTMLButtonElement>('#filterRefreshBtn')?.addEventListener('click', () => {
      this.rescanScene();
      this.renderUI();
    });
  }

  private renderParamsSection(obj: SceneObject): HTMLDivElement {
    const styles = getCheatUIStyles();
    const section = document.createElement('div');
    section.style.cssText = styles.sectionStyle;

    const filter = obj.filter ?? new Filter();
    const kind = filter.kind;

    const animateLabel = this.animFrameId !== null ? 'Stop animate' : 'Animate';
    const animateBtn = ANIMATED_KINDS.has(kind)
      ? `<button id="filterAnimateBtn" style="${styles.buttonStyle}">${animateLabel}</button>`
      : '';

    const isWrapped = obj instanceof FilteredSceneObject;
    const canWrap = !!this.renderDevice && !isWrapped;
    const wrapBtn = isWrapped
      ? `<button id="filterUnwrapBtn" style="${styles.buttonStyle}">Unwrap RT</button>`
      : `<button id="filterWrapBtn" style="${styles.buttonStyle}" ${canWrap ? '' : 'disabled title="RenderDevice not available"'}>Wrap in RT</button>`;
    const spriteSize = this.measureSpriteFrame(obj);
    const sizeLabel = spriteSize
      ? `sprite frame: ${spriteSize.x}×${spriteSize.y}`
      : 'sprite frame: unknown (not a SpriteSceneObject)';
    const wrapSizeRow = isWrapped
      ? ''
      : `
        <div style="margin:6px 0;font-size:11px;color:#888;">${sizeLabel}</div>
        <div style="display:flex;gap:8px;align-items:center;margin:6px 0;font-size:12px;color:#bbb;">
          <span style="flex:0 0 80px;">FBO size</span>
          <input id="filterWrapW" type="number" min="32" max="2048" step="32" value="${this.wrapWidth}" style="width:64px;background:#3a3a3a;color:white;border:1px solid #555;border-radius:4px;padding:2px 4px;"/>
          ×
          <input id="filterWrapH" type="number" min="32" max="2048" step="32" value="${this.wrapHeight}" style="width:64px;background:#3a3a3a;color:white;border:1px solid #555;border-radius:4px;padding:2px 4px;"/>
          <button id="filterAutoSizeBtn" style="${styles.buttonStyle}" ${spriteSize ? '' : 'disabled'} title="Auto-fit FBO from sprite frame size + 30% padding, rounded up to 32">Auto</button>
        </div>`;

    section.innerHTML = `
      <div style="font-size:13px;font-weight:bold;margin-bottom:4px;">${escapeHtml(obj.id)}${isWrapped ? ' <span style="color:#7fdc7a;font-size:10px;font-weight:normal;">[RT-wrapped]</span>' : ''}</div>
      <label style="display:block;font-size:12px;margin:4px 0;">
        Kind:
        <select id="filterKindSelect" style="${styles.selectStyle}">
          ${KIND_OPTIONS.map(
            (o) => `<option value="${o.value}" ${o.value === kind ? 'selected' : ''}>${o.label}</option>`
          ).join('')}
        </select>
      </label>
      <div id="filterParams"></div>
      ${wrapSizeRow}
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button id="filterRemoveBtn" style="${styles.buttonStyle}">Remove filter</button>
        ${animateBtn}
        ${wrapBtn}
      </div>
      <details style="margin-top:8px;">
        <summary style="cursor:pointer;color:#bbb;font-size:11px;">Show code</summary>
        <textarea readonly style="width:100%;min-height:140px;margin-top:4px;background:#1a1a1a;color:#d0d0d0;border:1px solid #444;border-radius:4px;padding:6px;font:11px monospace;resize:vertical;box-sizing:border-box;">${escapeHtml(this.snippetFor(obj, filter, isWrapped))}</textarea>
      </details>
    `;

    section.querySelector<HTMLSelectElement>('#filterKindSelect')?.addEventListener('change', (e) => {
      const newKind = parseInt((e.target as HTMLSelectElement).value, 10) as FilterKind;
      filter.kind = newKind;
      if (newKind === FilterKind.None) {
        obj.filter = null;
      } else {
        obj.filter = filter;
      }
      this.renderUI();
    });

    section.querySelector<HTMLButtonElement>('#filterRemoveBtn')?.addEventListener('click', () => {
      obj.filter = null;
      this.stopAnimLoop();
      this.renderUI();
    });

    section.querySelector<HTMLButtonElement>('#filterAnimateBtn')?.addEventListener('click', () => {
      if (this.animFrameId !== null) {
        this.stopAnimLoop();
      } else {
        this.startAnimLoop();
      }
      this.renderUI();
    });

    section.querySelector<HTMLInputElement>('#filterWrapW')?.addEventListener('input', (e) => {
      const v = parseInt((e.target as HTMLInputElement).value, 10);
      if (Number.isFinite(v) && v >= 32) this.wrapWidth = v;
    });
    section.querySelector<HTMLInputElement>('#filterWrapH')?.addEventListener('input', (e) => {
      const v = parseInt((e.target as HTMLInputElement).value, 10);
      if (Number.isFinite(v) && v >= 32) this.wrapHeight = v;
    });

    section.querySelector<HTMLButtonElement>('#filterAutoSizeBtn')?.addEventListener('click', () => {
      const sz = this.measureSpriteFrame(obj);
      if (!sz) return;
      const ceil32 = (n: number) => Math.max(32, Math.ceil((n * 1.3) / 32) * 32);
      this.wrapWidth = ceil32(sz.x);
      this.wrapHeight = ceil32(sz.y);
      this.renderUI();
    });

    section.querySelector<HTMLButtonElement>('#filterWrapBtn')?.addEventListener('click', () => {
      const wrapper = this.wrapInRenderTarget(obj);
      if (wrapper) {
        this.selectedRef = new WeakRef(wrapper);
        this.selectedId = wrapper.id;
        this.renderUI();
      }
    });

    section.querySelector<HTMLButtonElement>('#filterUnwrapBtn')?.addEventListener('click', () => {
      const inner = this.unwrapRenderTarget(obj as FilteredSceneObject);
      if (inner) {
        this.selectedRef = new WeakRef(inner);
        this.selectedId = inner.id;
      } else {
        this.selectedRef = null;
        this.selectedId = '';
      }
      this.renderUI();
    });

    const paramsDiv = section.querySelector<HTMLDivElement>('#filterParams')!;
    this.renderParamFields(paramsDiv, filter);
    if (filter.kind === FilterKind.Flash) {
      paramsDiv.appendChild(
        this.vec2Row('Quad position (px)', obj.position, (v) => (obj.position = v), -8192, 8192, 1)
      );
      paramsDiv.appendChild(
        this.numberRow('Quad scale', obj.scale.x, 0.05, 10, 0.05, (v) => (obj.scale = new Vector2(v, v)))
      );
    }
    return section;
  }

  private measureSpriteFrame(obj: SceneObject): { x: number; y: number } | null {
    const anyObj = obj as unknown as {
      spriteResource?: { data?: { frames?: Array<{ srcRect?: { width: number; height: number } }> } };
      frame?: number;
    };
    const frames = anyObj.spriteResource?.data?.frames;
    if (!frames || frames.length === 0) return null;
    const idx = Math.max(0, Math.min(frames.length - 1, anyObj.frame ?? 0));
    const r = frames[idx]?.srcRect;
    if (!r || r.width === 0 || r.height === 0) return null;
    return { x: Math.round(r.width), y: Math.round(r.height) };
  }

  private wrapInRenderTarget(obj: SceneObject): FilteredSceneObject | null {
    if (!this.renderDevice) return null;
    const parent = obj.parent;
    if (!parent) return null;
    const w = this.wrapWidth;
    const h = this.wrapHeight;
    const wrapper = new FilteredSceneObject(this.renderDevice, w, h);
    wrapper.id = `${obj.id}__rt`;
    wrapper.position = obj.position.clone();
    wrapper.filter = obj.filter ?? null;

    const parentChilds = (parent as unknown as { _childs: SceneObject[] })._childs;
    const idx = parentChilds.indexOf(obj);
    parent.removeChild(obj);
    obj.position = new Vector2(0, 0);
    obj.filter = null;
    wrapper.addChild(obj);
    parent.addChild(wrapper);

    if (idx >= 0) {
      const wrapperIdx = parentChilds.indexOf(wrapper);
      if (wrapperIdx !== -1 && wrapperIdx !== idx) {
        parentChilds.splice(wrapperIdx, 1);
        parentChilds.splice(idx, 0, wrapper);
      }
    }

    try {
      wrapper.initialize();
    } catch {
      //
    }
    return wrapper;
  }

  private unwrapRenderTarget(wrapper: FilteredSceneObject): SceneObject | null {
    const parent = wrapper.parent;
    if (!parent) return null;
    const childs = wrapper.innerChilds;
    if (!childs || childs.length === 0) return null;
    const inner = childs[0];

    const parentChilds = (parent as unknown as { _childs: SceneObject[] })._childs;
    const idx = parentChilds.indexOf(wrapper);

    wrapper.removeChild(inner);
    parent.removeChild(wrapper);
    inner.position = wrapper.position.clone();
    inner.filter = wrapper.filter ?? null;
    parent.addChild(inner);

    if (idx >= 0) {
      const innerIdx = parentChilds.indexOf(inner);
      if (innerIdx !== -1 && innerIdx !== idx) {
        parentChilds.splice(innerIdx, 1);
        parentChilds.splice(idx, 0, inner);
      }
    }

    try {
      wrapper.deinitializeImpl?.();
    } catch {
      //
    }
    return inner;
  }

  private spawnFlashQuad(): void {
    if (!this.renderDevice) return;
    const resources = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    const canvas = document.createElement('canvas');
    canvas.width = FLASH_QUAD_SIZE;
    canvas.height = FLASH_QUAD_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, FLASH_QUAD_SIZE, FLASH_QUAD_SIZE);

    const quad = new ExternalSceneObject(
      canvas,
      this.renderDevice,
      new BlendState(true, Blend.One, Blend.One)
    );
    quad.id = `flash_quad_${++this.flashQuadCounter}`;
    quad.sceneUrl = FLASH_QUAD_SCENE_GROUP;
    // pivot в центре: position — это центр вспышки, scale растёт симметрично во все стороны
    quad.pivot = new Vector2(FLASH_QUAD_SIZE / 2, FLASH_QUAD_SIZE / 2);
    quad.position = FLASH_QUAD_DEFAULT_POSITION.clone();
    quad.z = FLASH_QUAD_Z;

    const f = new Filter();
    f.kind = FilterKind.Flash;
    f.radius = FLASH_PRESET_RADIUS;
    f.strength = FLASH_PRESET_CORE_RATIO;
    f.tintColor = FLASH_PRESET_TINT;
    f.angle = FLASH_PRESET_RING_RADIUS;
    f.pixelSize = FLASH_PRESET_RING_WIDTH_PCT;
    f.sepia = FLASH_PRESET_RING_INTENSITY;
    quad.filter = f;

    resources.root.addChild(quad);
    // Без rescanScene: полный обход сцены не нужен, просто дописываем квад в снапшот
    this.snapshot.push({ id: quad.id, sceneUrl: FLASH_QUAD_SCENE_GROUP, ref: new WeakRef(quad) });
    if (!this.sceneUrls.includes(FLASH_QUAD_SCENE_GROUP)) this.sceneUrls.push(FLASH_QUAD_SCENE_GROUP);
    this.selectedRef = new WeakRef(quad);
    this.selectedId = quad.id;
    this.renderUI();
  }

  private snippetFor(obj: SceneObject, filter: Filter, isWrapped: boolean): string {
    const kindName = FilterKind[filter.kind] ?? 'None';
    const lines: string[] = [];
    lines.push(`// Apply ${kindName} to '${obj.id}'`);
    if (filter.kind === FilterKind.None) {
      lines.push(`obj.filter = null;`);
      return lines.join('\n');
    }
    if (filter.kind === FilterKind.Flash && obj instanceof ExternalSceneObject) {
      const fmtN = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(2));
      lines.push(`// Квад-носитель: выделенная белая текстура + аддитивный блендинг`);
      lines.push(`const canvas = document.createElement('canvas');`);
      lines.push(`canvas.width = ${FLASH_QUAD_SIZE};`);
      lines.push(`canvas.height = ${FLASH_QUAD_SIZE};`);
      lines.push(`const ctx = canvas.getContext('2d')!;`);
      lines.push(`ctx.fillStyle = '#ffffff';`);
      lines.push(`ctx.fillRect(0, 0, ${FLASH_QUAD_SIZE}, ${FLASH_QUAD_SIZE});`);
      lines.push(`const quad = new ExternalSceneObject(canvas, renderDevice, new BlendState(true, Blend.One, Blend.One));`);
      lines.push(`quad.pivot = new Vector2(${FLASH_QUAD_SIZE / 2}, ${FLASH_QUAD_SIZE / 2}); // position = центр вспышки`);
      lines.push(`quad.position = new Vector2(${fmtN(obj.position.x)}, ${fmtN(obj.position.y)});`);
      lines.push(`quad.scale = new Vector2(${fmtN(obj.scale.x)}, ${fmtN(obj.scale.y)});`);
      lines.push(`quad.z = ${fmtN(obj.z)}; // поверх соседей по родителю`);
      lines.push(`parent.addChild(quad);`);
      lines.push(``);
    }
    if (isWrapped) {
      lines.push(`// Wrapped in FilteredSceneObject (RenderTarget pass)`);
      const w = (obj as FilteredSceneObject).width;
      const h = (obj as FilteredSceneObject).height;
      lines.push(`const wrapper = new FilteredSceneObject(renderDevice, ${w}, ${h});`);
      lines.push(`wrapper.position = original.position.clone();`);
      lines.push(`original.position = new Vector2(0, 0);`);
      lines.push(`parent.removeChild(original);`);
      lines.push(`wrapper.addChild(original);`);
      lines.push(`parent.addChild(wrapper);`);
      lines.push(``);
      lines.push(`const f = new Filter();`);
      lines.push(`f.kind = FilterKind.${kindName};`);
    } else {
      lines.push(`const f = new Filter();`);
      lines.push(`f.kind = FilterKind.${kindName};`);
    }
    this.serializeFilterFields(filter, lines);
    if (isWrapped) {
      lines.push(`wrapper.filter = f;`);
    } else if (filter.kind === FilterKind.Flash && obj instanceof ExternalSceneObject) {
      lines.push(`quad.filter = f;`);
    } else {
      lines.push(`obj.filter = f;`);
    }
    if (filter.kind === FilterKind.Flash) {
      lines.push(``);
      lines.push(`// Flash рисуется на отдельном кваде с выделенной белой текстурой,`);
      lines.push(`// позиция/размер — через сам SceneObject. Пульс — анимация intensity:`);
      lines.push(`const base = f.intensity;`);
      lines.push(`(function tick(){`);
      lines.push(`  const t = (performance.now() / 1000) % ${FLASH_PULSE_PERIOD_SEC};`);
      lines.push(`  const e = t < ${FLASH_PULSE_ATTACK_SEC} ? t / ${FLASH_PULSE_ATTACK_SEC}`);
      lines.push(`    : t < ${FLASH_PULSE_ATTACK_SEC + FLASH_PULSE_DECAY_SEC} ? (1 - (t - ${FLASH_PULSE_ATTACK_SEC}) / ${FLASH_PULSE_DECAY_SEC}) ** 2 : 0;`);
      lines.push(`  f.intensity = base * e;`);
      lines.push(`  requestAnimationFrame(tick);`);
      lines.push(`})();`);
    } else if (ANIMATED_KINDS.has(filter.kind)) {
      lines.push(``);
      lines.push(`// Animate time per frame:`);
      lines.push(`(function tick(){ f.time = performance.now()/1000; requestAnimationFrame(tick); })();`);
    }
    return lines.join('\n');
  }

  private serializeFilterFields(filter: Filter, lines: string[]): void {
    const fmt = (n: number) => Number.isInteger(n) ? `${n}` : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0');
    const c = (v: Color4) => `new Color4(${fmt(v.r)}, ${fmt(v.g)}, ${fmt(v.b)}, ${fmt(v.a)})`;
    const v2 = (v: Vector2) => `new Vector2(${fmt(v.x)}, ${fmt(v.y)})`;
    switch (filter.kind) {
      case FilterKind.Tint:
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.Pixelate:
        lines.push(`f.pixelSize = ${fmt(filter.pixelSize)};`);
        break;
      case FilterKind.Shockwave:
        lines.push(`f.center = ${v2(filter.center)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        break;
      case FilterKind.Noise:
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.Twist:
        lines.push(`f.center = ${v2(filter.center)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.angle = ${fmt(filter.angle)};`);
        break;
      case FilterKind.Bulge:
        lines.push(`f.center = ${v2(filter.center)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        break;
      case FilterKind.RGBSplit:
        lines.push(`f.redOffset = ${v2(filter.redOffset)};`);
        lines.push(`f.greenOffset = ${v2(filter.greenOffset)};`);
        lines.push(`f.blueOffset = ${v2(filter.blueOffset)};`);
        break;
      case FilterKind.CRT:
        lines.push(`f.pixelSize = ${fmt(filter.pixelSize)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        break;
      case FilterKind.OldFilm:
        lines.push(`f.sepia = ${fmt(filter.sepia)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.Outline:
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        break;
      case FilterKind.InnerGlow:
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.Vignette:
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.GodRays:
        lines.push(`f.center = ${v2(filter.center)};`);
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.PseudoBloom:
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        break;
      case FilterKind.HueShift:
        lines.push(`f.angle = ${fmt(filter.angle)};`);
        break;
      case FilterKind.Posterize:
        lines.push(`f.pixelSize = ${fmt(filter.pixelSize)};`);
        break;
      case FilterKind.Flash:
        lines.push(`f.center = ${v2(filter.center)};`);
        lines.push(`f.radius = ${fmt(filter.radius)};`);
        lines.push(`f.strength = ${fmt(filter.strength)};`);
        lines.push(`f.tintColor = ${c(filter.tintColor)};`);
        lines.push(`f.intensity = ${fmt(filter.intensity)};`);
        lines.push(`f.angle = ${fmt(filter.angle)}; // радиус кольца (0 = выкл)`);
        lines.push(`f.pixelSize = ${fmt(filter.pixelSize)}; // ширина кольца, % от радиуса`);
        lines.push(`f.sepia = ${fmt(filter.sepia)}; // яркость кольца`);
        break;
    }
  }

  private renderParamFields(parent: HTMLDivElement, filter: Filter): void {
    parent.innerHTML = '';
    switch (filter.kind) {
      case FilterKind.Tint:
        parent.appendChild(this.colorRow('Tint color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 2, 0.05, (v) => (filter.intensity = v)));
        break;
      case FilterKind.Pixelate:
        parent.appendChild(this.numberRow('Pixel size (cells per UV)', filter.pixelSize, 4, 200, 1, (v) => (filter.pixelSize = v)));
        break;
      case FilterKind.Shockwave:
        parent.appendChild(this.vec2Row('Center (UV)', filter.center, (v) => (filter.center = v), 0, 1, 0.01));
        parent.appendChild(this.numberRow('Radius', filter.radius, 0, 1, 0.01, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Strength', filter.strength, 0, 5, 0.05, (v) => (filter.strength = v)));
        break;
      case FilterKind.Noise:
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 1, 0.01, (v) => (filter.intensity = v)));
        break;
      case FilterKind.Twist:
        parent.appendChild(this.vec2Row('Center (UV)', filter.center, (v) => (filter.center = v), 0, 1, 0.01));
        parent.appendChild(this.numberRow('Radius', filter.radius, 0, 1, 0.01, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Angle (rad)', filter.angle, -10, 10, 0.05, (v) => (filter.angle = v)));
        break;
      case FilterKind.Bulge:
        parent.appendChild(this.vec2Row('Center (UV)', filter.center, (v) => (filter.center = v), 0, 1, 0.01));
        parent.appendChild(this.numberRow('Radius', filter.radius, 0, 1, 0.01, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Strength (>0 bulge, <0 pinch)', filter.strength, -1, 1, 0.01, (v) => (filter.strength = v)));
        break;
      case FilterKind.RGBSplit:
        parent.appendChild(this.vec2Row('Red offset', filter.redOffset, (v) => (filter.redOffset = v), -0.05, 0.05, 0.001));
        parent.appendChild(this.vec2Row('Green offset', filter.greenOffset, (v) => (filter.greenOffset = v), -0.05, 0.05, 0.001));
        parent.appendChild(this.vec2Row('Blue offset', filter.blueOffset, (v) => (filter.blueOffset = v), -0.05, 0.05, 0.001));
        break;
      case FilterKind.CRT:
        parent.appendChild(this.numberRow('Scanline density', filter.pixelSize, 50, 800, 5, (v) => (filter.pixelSize = v)));
        parent.appendChild(this.numberRow('Scanline depth', filter.intensity, 0, 1, 0.01, (v) => (filter.intensity = v)));
        parent.appendChild(this.numberRow('Vignette', filter.strength, 0, 2, 0.05, (v) => (filter.strength = v)));
        break;
      case FilterKind.OldFilm:
        parent.appendChild(this.numberRow('Sepia', filter.sepia, 0, 1, 0.01, (v) => (filter.sepia = v)));
        parent.appendChild(this.numberRow('Scratch density', filter.intensity, 0, 1, 0.01, (v) => (filter.intensity = v)));
        break;
      case FilterKind.Outline:
        parent.appendChild(this.colorRow('Outline color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Thickness (UV)', filter.radius, 0.001, 0.05, 0.001, (v) => (filter.radius = v)));
        break;
      case FilterKind.InnerGlow:
        parent.appendChild(this.colorRow('Glow color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Width (UV)', filter.radius, 0.005, 0.2, 0.005, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 3, 0.05, (v) => (filter.intensity = v)));
        break;
      case FilterKind.Vignette:
        parent.appendChild(this.colorRow('Edge color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Inner radius', filter.radius, 0, 1, 0.01, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Falloff', filter.strength, 0, 1, 0.01, (v) => (filter.strength = v)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 1, 0.01, (v) => (filter.intensity = v)));
        break;
      case FilterKind.GodRays:
        parent.appendChild(this.vec2Row('Source (UV)', filter.center, (v) => (filter.center = v), -0.5, 1.5, 0.01));
        parent.appendChild(this.colorRow('Ray color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 5, 0.05, (v) => (filter.intensity = v)));
        break;
      case FilterKind.PseudoBloom:
        parent.appendChild(this.colorRow('Bloom color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Threshold', filter.strength, 0, 1, 0.01, (v) => (filter.strength = v)));
        parent.appendChild(this.numberRow('Sample radius', filter.radius, 0.001, 0.02, 0.0005, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 5, 0.05, (v) => (filter.intensity = v)));
        break;
      case FilterKind.HueShift:
        parent.appendChild(this.numberRow('Angle (rad)', filter.angle, -3.14, 3.14, 0.05, (v) => (filter.angle = v)));
        break;
      case FilterKind.Posterize:
        parent.appendChild(this.numberRow('Levels per channel', filter.pixelSize, 2, 32, 1, (v) => (filter.pixelSize = v)));
        break;
      case FilterKind.Flash:
        parent.appendChild(this.vec2Row('Center (UV)', filter.center, (v) => (filter.center = v), 0, 1, 0.01));
        parent.appendChild(this.numberRow('Halo radius', filter.radius, 0, 1, 0.01, (v) => (filter.radius = v)));
        parent.appendChild(this.numberRow('Core ratio', filter.strength, 0, 1, 0.01, (v) => (filter.strength = v)));
        parent.appendChild(this.colorRow('Halo color', filter.tintColor, (c) => (filter.tintColor = c)));
        parent.appendChild(this.numberRow('Intensity', filter.intensity, 0, 3, 0.05, (v) => (filter.intensity = v)));
        parent.appendChild(this.numberRow('Ring radius (UV, 0=off)', filter.angle, 0, 0.9, 0.01, (v) => (filter.angle = v)));
        parent.appendChild(this.numberRow('Ring width (% of R)', filter.pixelSize, 1, 40, 0.5, (v) => (filter.pixelSize = v)));
        parent.appendChild(this.numberRow('Ring intensity', filter.sepia, 0, 1.5, 0.05, (v) => (filter.sepia = v)));
        break;
      default:
        break;
    }
  }

  private numberRow(
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onChange: (v: number) => void
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = 'margin:6px 0; font-size:12px;';
    row.innerHTML = `
      <label style="display:flex;align-items:center;gap:8px;">
        <span style="flex:0 0 140px;color:#bbb;">${label}</span>
        <input type="range" min="${min}" max="${max}" step="${step}" value="${value}" style="flex:1;"/>
        <input type="number" min="${min}" max="${max}" step="${step}" value="${value}" style="width:64px;background:#3a3a3a;color:white;border:1px solid #555;border-radius:4px;padding:2px 4px;"/>
      </label>
    `;
    const range = row.querySelector<HTMLInputElement>('input[type=range]')!;
    const num = row.querySelector<HTMLInputElement>('input[type=number]')!;
    range.addEventListener('input', () => {
      num.value = range.value;
      onChange(parseFloat(range.value));
    });
    num.addEventListener('input', () => {
      range.value = num.value;
      onChange(parseFloat(num.value));
    });
    return row;
  }

  private vec2Row(
    label: string,
    value: Vector2,
    onChange: (v: Vector2) => void,
    min: number,
    max: number,
    step: number
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = 'margin:6px 0; font-size:12px;';
    row.innerHTML = `
      <div style="color:#bbb;margin-bottom:4px;">${label}</div>
      <div style="display:flex;gap:8px;">
        <label style="flex:1;display:flex;align-items:center;gap:4px;">
          x <input type="number" min="${min}" max="${max}" step="${step}" value="${value.x}" style="flex:1;background:#3a3a3a;color:white;border:1px solid #555;border-radius:4px;padding:2px 4px;"/>
        </label>
        <label style="flex:1;display:flex;align-items:center;gap:4px;">
          y <input type="number" min="${min}" max="${max}" step="${step}" value="${value.y}" style="flex:1;background:#3a3a3a;color:white;border:1px solid #555;border-radius:4px;padding:2px 4px;"/>
        </label>
      </div>
    `;
    const inputs = row.querySelectorAll<HTMLInputElement>('input[type=number]');
    inputs[0].addEventListener('input', () => {
      onChange(new Vector2(parseFloat(inputs[0].value), parseFloat(inputs[1].value)));
    });
    inputs[1].addEventListener('input', () => {
      onChange(new Vector2(parseFloat(inputs[0].value), parseFloat(inputs[1].value)));
    });
    return row;
  }

  private colorRow(label: string, value: Color4, onChange: (c: Color4) => void): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = 'margin:6px 0; font-size:12px;';
    const hex = color4ToHex(value);
    row.innerHTML = `
      <label style="display:flex;align-items:center;gap:8px;">
        <span style="flex:0 0 140px;color:#bbb;">${label}</span>
        <input type="color" value="${hex}" style="width:48px;height:24px;border:0;background:transparent;"/>
        <input type="range" min="0" max="1" step="0.01" value="${value.a}" style="flex:1;" title="amount (alpha)"/>
        <span class="alphaLabel" style="width:32px;text-align:right;color:#888;">${value.a.toFixed(2)}</span>
      </label>
    `;
    const colorInput = row.querySelector<HTMLInputElement>('input[type=color]')!;
    const alphaInput = row.querySelector<HTMLInputElement>('input[type=range]')!;
    const alphaLabel = row.querySelector<HTMLSpanElement>('.alphaLabel')!;
    const apply = () => {
      const c = hexToColor4(colorInput.value, parseFloat(alphaInput.value));
      alphaLabel.textContent = c.a.toFixed(2);
      onChange(c);
    };
    colorInput.addEventListener('input', apply);
    alphaInput.addEventListener('input', apply);
    return row;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function color4ToHex(c: Color4): string {
  const to = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${to(c.r)}${to(c.g)}${to(c.b)}`;
}

function hexToColor4(hex: string, alpha: number): Color4 {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return new Color4(1, 1, 1, alpha);
  const n = parseInt(m[1], 16);
  return new Color4(((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255, alpha);
}
